// API client for the system-wide WhatsApp (Baileys) connection. Super-admin
// only. The link flow: connect() → poll getStatus() until `qr` appears → scan
// it with the phone's WhatsApp → status flips to 'connected'.

import { apiFetch } from './api';

export type WhatsAppStatus = 'connected' | 'disconnected' | 'connecting' | 'qr_pending';

export interface WhatsAppInfo {
  status: WhatsAppStatus;
  phoneNumber?: string;
  phoneName?: string;
  /** data:image/png;base64 QR — present only while status==='qr_pending'. */
  qr?: string;
  connectedAt?: string;
}

export const getWhatsAppStatus = () =>
  apiFetch<WhatsAppInfo>('/api/admin/whatsapp/status');

export const connectWhatsApp = () =>
  apiFetch<WhatsAppInfo>('/api/admin/whatsapp/connect', { method: 'POST' });

export const disconnectWhatsApp = () =>
  apiFetch<{ ok: boolean }>('/api/admin/whatsapp/disconnect', { method: 'POST' });

// ─── Cloud API (official) ───
// Notifications go out as one approved template whose single body variable
// carries the rendered webhook text. The access token is write-only.

export type WhatsAppProvider = 'cloud' | 'baileys';

export interface WhatsAppCloudConfig {
  /** As stored; null = automatic (Cloud API once configured). */
  provider: WhatsAppProvider | null;
  effectiveProvider: WhatsAppProvider;
  phoneNumberId: string | null;
  templateName: string | null;
  templateLanguage: string | null;
  tokenSet: boolean;
  tokenLast4: string | null;
  configured: boolean;
  encryptionAvailable: boolean;
}

export interface WhatsAppCloudPatch {
  provider?: WhatsAppProvider | null;
  phoneNumberId?: string | null;
  /** Non-empty sets it, null clears it, omitted leaves it. */
  accessToken?: string | null;
  templateName?: string | null;
  templateLanguage?: string | null;
}

export type WhatsAppCloudResult =
  | { ok: true; displayPhoneNumber?: string; verifiedName?: string; qualityRating?: string | null; messageId?: string }
  | { ok: false; detail: string };

export const getWhatsAppCloud = () =>
  apiFetch<WhatsAppCloudConfig>('/api/admin/whatsapp/cloud');

export const saveWhatsAppCloud = (patch: WhatsAppCloudPatch) =>
  apiFetch<WhatsAppCloudConfig>('/api/admin/whatsapp/cloud', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });

export const checkWhatsAppCloud = () =>
  apiFetch<WhatsAppCloudResult>('/api/admin/whatsapp/cloud/check', { method: 'POST' });

export const testWhatsAppCloud = (phone: string) =>
  apiFetch<WhatsAppCloudResult>('/api/admin/whatsapp/cloud/test', {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });
